import React, { useEffect, useState, useRef } from "react";
import Context from "../../Context";
import { useParams, Link } from "react-router-dom";
import { Header, UserPreview } from "../../components/modules";
import { useToast } from "../../components/ui";

// Icons
import { IoReceiptOutline } from "react-icons/io5";
import {
    FaGift,
    FaTicket,
    FaLayerGroup,
    FaCopy,
    FaCheck,
    FaArrowLeft,
    FaReceipt,
    FaCalendarDays,
    FaRegCircleCheck,
    FaClock,
    FaTag,
    FaImages,
    FaClockRotateLeft,
    FaMagnifyingGlass,
} from "react-icons/fa6";
import { TbExternalLink } from "react-icons/tb";

export const MissionDetail = () => {
    const { callApi } = Context();
    const { missionId } = useParams();
    const { toast } = useToast();

    // Main Data States
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(false);
    const userFetchedRef = useRef(false);

    // Extra Details States (Current Transaction)
    const [couponData, setCouponData] = useState(null);
    const [loadingCoupon, setLoadingCoupon] = useState(false);
    const [lotteryOrders, setLotteryOrders] = useState([]);
    const [loadingLotteryOrders, setLoadingLotteryOrders] = useState(false);

    // User History States (Cross-system lifetime logs)
    const [activeTab, setActiveTab] = useState("missions"); // 'missions' | 'coupons' | 'lotteryOrders'
    const [userMissions, setUserMissions] = useState([]);
    const [loadingUserMissions, setLoadingUserMissions] = useState(false);
    const [userCoupons, setUserCoupons] = useState([]);
    const [loadingUserCoupons, setLoadingUserCoupons] = useState(false);
    const [userLotteryOrders, setUserLotteryOrders] = useState([]);
    const [loadingUserLotteryOrders, setLoadingUserLotteryOrders] = useState(false);

    // UI States
    const [copiedKey, setCopiedKey] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [historySearch, setHistorySearch] = useState("");

    // Copy helper
    const handleCopy = (text, key) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedKey(key);
        toast({
            className: "bg-emerald-600 text-white font-semibold",
            title: `คัดลอก ${text} เรียบร้อยแล้ว`,
        });
        setTimeout(() => setCopiedKey(null), 2000);
    };

    // Format date helper
    const formatDateTime = (timestamp) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return "-";
        return (
            date.toLocaleDateString("th-TH", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }) + " น."
        );
    };

    // Fetch Mission Order Data
    const getOrderDetails = async () => {
        setLoading(true);
        try {
            let res = await callApi(
                "missions-getAllMissionOrders",
                {
                    roundDate: null,
                    queries: [["id", "==", missionId]],
                },
                { notShowLoading: true }
            );

            let orderData = res && res.length > 0 ? res[0] : null;

            if (!orderData) {
                const resByShortId = await callApi(
                    "missions-getAllMissionOrders",
                    {
                        roundDate: null,
                        queries: [["shortId", "==", missionId]],
                    },
                    { notShowLoading: true }
                );
                if (resByShortId && resByShortId.length > 0) {
                    orderData = resByShortId[0];
                }
            }

            if (orderData) {
                setOrder(orderData);

                // Fetch User Info & Lifetime History
                if (orderData.userId && !userFetchedRef.current) {
                    userFetchedRef.current = true;
                    getUser(orderData.userId);
                    fetchUserAllHistories(orderData.userId);
                }

                // Fetch Associated Coupon Data for this transaction
                if (orderData.rewardId) {
                    getCouponDetails(orderData.rewardId);
                }

                // Fetch Associated Lottery Orders Data for this transaction
                if (
                    Array.isArray(orderData.orderReference) &&
                    orderData.orderReference.length > 0
                ) {
                    getLotteryOrdersDetails(orderData.orderReference);
                }
            } else {
                toast({
                    className: "bg-[#c15b78] text-white",
                    title: "ไม่พบข้อมูลคำขอแลกรางวัล",
                });
            }
        } catch (err) {
            console.error("Error fetching mission order:", err);
            toast({
                className: "bg-[#c15b78] text-white",
                title: "เกิดข้อผิดพลาดในการดึงข้อมูล",
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch User Info
    const getUser = async (userId) => {
        try {
            const { status, data } = await callApi(
                "users-getUserInfoAdmin",
                { userId },
                { notShowLoading: true }
            );
            if (status && data) {
                setUser(data);
            }
        } catch (err) {
            console.error("Error fetching user:", err);
        }
    };

    // Fetch Coupon Details for current transaction
    const getCouponDetails = async (rewardId) => {
        setLoadingCoupon(true);
        try {
            const res = await callApi(
                "coupons-getCouponOrder",
                { orderId: rewardId },
                { notShowLoading: true }
            );
            if (res && res.status && res.data) {
                setCouponData(res.data);
            }
        } catch (err) {
            console.error("Error fetching coupon:", err);
        } finally {
            setLoadingCoupon(false);
        }
    };

    // Fetch Referenced Lottery Orders Details for current transaction
    const getLotteryOrdersDetails = async (orderIds) => {
        setLoadingLotteryOrders(true);
        try {
            const orderPromises = orderIds.map(async (id) => {
                try {
                    const res = await callApi(
                        "orders-getOrderInfoAdmin",
                        { id },
                        { notShowLoading: true }
                    );
                    if (res && res.status && res.order) {
                        return res.order;
                    }
                } catch (e) {
                    console.warn(`Failed to fetch order ${id}:`, e);
                }
                return { id, isFallback: true };
            });

            const results = await Promise.all(orderPromises);
            setLotteryOrders(results.filter(Boolean));
        } catch (err) {
            console.error("Error fetching lottery orders:", err);
        } finally {
            setLoadingLotteryOrders(false);
        }
    };

    // Fetch All Customer Lifetime Histories (Missions, Coupons, Lottery Orders)
    const fetchUserAllHistories = async (userId) => {
        // 1. Fetch User Missions History
        setLoadingUserMissions(true);
        try {
            const res = await callApi(
                "missions-getAllMissionOrders",
                {
                    roundDate: null,
                    queries: [["userId", "==", userId]],
                },
                { notShowLoading: true }
            );
            if (Array.isArray(res)) {
                setUserMissions(res);
            }
        } catch (err) {
            console.error("Error fetching user missions history:", err);
        } finally {
            setLoadingUserMissions(false);
        }

        // 2. Fetch User Coupons History
        setLoadingUserCoupons(true);
        try {
            const res = await callApi(
                "coupons-getAllCouponOrders",
                {
                    roundDate: false,
                    queries: [["userId", "==", userId]],
                    limit: 100,
                    orderBy: ["createdAt", "desc"],
                },
                { notShowLoading: true }
            );
            if (Array.isArray(res)) {
                setUserCoupons(res);
            } else if (res && Array.isArray(res.data)) {
                setUserCoupons(res.data);
            }
        } catch (err) {
            console.error("Error fetching user coupons history:", err);
        } finally {
            setLoadingUserCoupons(false);
        }

        // 3. Fetch User Lottery Orders History
        setLoadingUserLotteryOrders(true);
        try {
            const res = await callApi(
                "orders-getAll",
                {
                    findAll: true,
                    queries: [["userId", userId]],
                    limit: 100,
                    orderBy: ["createdAt", "desc"],
                },
                { notShowLoading: true }
            );
            if (Array.isArray(res)) {
                setUserLotteryOrders(res);
            } else if (res && Array.isArray(res.data)) {
                setUserLotteryOrders(res.data);
            }
        } catch (err) {
            console.error("Error fetching user lottery orders history:", err);
        } finally {
            setLoadingUserLotteryOrders(false);
        }
    };

    useEffect(() => {
        if (missionId) {
            getOrderDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missionId]);

    // Mission Type Label Helper (Matches manageMission.jsx translations)
    const getMissionTypeBadge = (type) => {
        switch (type) {
            case "amountInOneOrder":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                        🎯 จำนวนใบใน 1 ออเดอร์
                    </span>
                );
            case "lastTwoInOneOrder":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                        🔢 ระบุเลขท้าย 2 ตัว (1 ออเดอร์)
                    </span>
                );
            case "lastTwoInMultipleOrders":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        🔄 ระบุเลขท้าย 2 ตัว (สะสมหลายออเดอร์)
                    </span>
                );
            case "setInMultipleOrder":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        📦 หวยชุด (สะสมหลายออเดอร์)
                    </span>
                );
            case "custom":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                        ⚙️ ภารกิจกำหนดเอง (Custom)
                    </span>
                );
            case "buy":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                        🎯 ซื้อสลากตามเงื่อนไข
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200">
                        <FaTag className="text-[11px]" /> {type || "ภารกิจทั่วไป"}
                    </span>
                );
        }
    };

    // Filtered Histories based on search
    const filteredUserMissions = userMissions.filter((m) => {
        if (!historySearch.trim()) return true;
        const q = historySearch.toLowerCase();
        return (
            m.shortId?.toLowerCase().includes(q) ||
            m.id?.toLowerCase().includes(q) ||
            m.mission?.name?.toLowerCase().includes(q) ||
            m.roundDate?.toLowerCase().includes(q)
        );
    });

    const filteredUserCoupons = userCoupons.filter((c) => {
        if (!historySearch.trim()) return true;
        const q = historySearch.toLowerCase();
        return (
            c.shortId?.toLowerCase().includes(q) ||
            c.id?.toLowerCase().includes(q) ||
            c.coupon?.name?.toLowerCase().includes(q) ||
            c.coupon?.type?.toLowerCase().includes(q)
        );
    });

    const filteredUserLotteryOrders = userLotteryOrders.filter((o) => {
        if (!historySearch.trim()) return true;
        const q = historySearch.toLowerCase();
        const hasMatchingLotto = o.lotteries?.some((l) =>
            String(l.number || l.id || l || "").includes(q)
        );
        return (
            o.shortId?.toLowerCase().includes(q) ||
            o.id?.toLowerCase().includes(q) ||
            o.roundDate?.toLowerCase().includes(q) ||
            o.status?.toLowerCase().includes(q) ||
            hasMatchingLotto
        );
    });

    // Calculate totals for history summaries
    const totalUserLottoSpent = userLotteryOrders.reduce(
        (sum, o) => sum + (Number(o.totalPrice) || 0),
        0
    );
    const totalUserLottoTickets = userLotteryOrders.reduce(
        (sum, o) => sum + (o.lotteries?.length || o.size || 0),
        0
    );

    // Loading State
    if (loading) {
        return (
            <div className="w-full min-h-[600px] flex flex-col justify-center items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-rose-500 border-t-transparent"></div>
                <p className="text-sm font-semibold text-gray-500">กำลังโหลดข้อมูลภารกิจ...</p>
            </div>
        );
    }

    // Not Found State
    if (!order) {
        return (
            <div className="w-full max-w-[1400px] mx-auto p-6">
                <Link
                    to="/mission/orders"
                    className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-navy mb-6 transition-colors"
                >
                    <FaArrowLeft /> ย้อนกลับไปประวัติคำขอภารกิจ
                </Link>
                <div className="bg-white rounded-md shadow-md border border-gray-200 p-12 text-center">
                    <IoReceiptOutline className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <h2 className="text-xl font-bold text-gray-700 mb-1">ไม่พบข้อมูลคำขอแลกรางวัล</h2>
                    <p className="text-sm font-semibold text-gray-400">
                        รหัสคำขอ "{missionId}" อาจไม่มีอยู่ในระบบ หรือถูกลบไปแล้ว
                    </p>
                </div>
            </div>
        );
    }

    const mission = order.mission || {};
    const missionReward = mission.reward || {};
    const hasRefOrders = Array.isArray(order.orderReference) && order.orderReference.length > 0;
    const currentTotalLottoTickets = lotteryOrders.reduce(
        (acc, curr) => acc + (curr.lotteries?.length || curr.size || 0),
        0
    );
    const currentTotalLottoPrice = lotteryOrders.reduce(
        (acc, curr) => acc + (Number(curr.totalPrice) || 0),
        0
    );

    return (
        <div className="h-fit w-full min-w-0 max-w-[1600px] mx-auto p-4 sm:p-6 space-y-4">
            {/* Header */}
            <Header
                header="Missions"
                subHeader={`รายละเอียดคำขอแลกรางวัล `}
                icon={<IoReceiptOutline className="text-blue" />}
            />

            {/* Minimal Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 px-1">

                <Link
                    to="/mission/orders"
                    className="hover:text-rose-600 transition-colors text-gray-500 hover:underline"
                >
                    รายการคำขอแลกรางวัล
                </Link>
                <span className="text-gray-300">/</span>
                <span className="text-gray-800 font-bold font-mono">
                    #{order.shortId || order.id?.slice(-5).toUpperCase()}
                </span>
            </nav>

            {/* Customer Profile Section */}
            <UserPreview user={user} />

            {/* ========================================================================= */}
            {/* UNIFIED COMPACT PANEL: Mission Info + Reward Coupon + Referenced Lotto Orders */}
            {/* ========================================================================= */}
            <div className="bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
                {/* Panel Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between p-4 border-b border-gray-200 bg-gray-50/70 gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-md bg-rose-500 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                            <FaLayerGroup />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-xl font-bold text-gray-900">
                                    รายละเอียดคำขอรับรางวัล #{order.shortId || order.id?.slice(-5).toUpperCase()}
                                </h2>
                                {getMissionTypeBadge(mission.type)}
                            </div>
                            <p className="text-xs text-gray-500 font-semibold mt-0.5">
                                ข้อมูลภารกิจ ของรางวัลคูปอง และคำสั่งซื้อสลากที่ใช้แลกในรายการนี้
                            </p>
                        </div>
                    </div>

                    {/* Meta Badges & Quick Actions */}
                    <div className="flex items-center gap-2 flex-wrap self-start lg:self-auto">
                        {order.roundDate && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-white text-gray-700 border border-gray-200 shadow-2xs">
                                <FaCalendarDays className="text-gray-500 text-[11px]" />
                                งวด {order.roundDate}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                            <FaRegCircleCheck className="text-emerald-500 text-[11px]" />
                            สำเร็จ (ระบบอัตโนมัติ)
                        </span>
                        <button
                            onClick={() => handleCopy(order.id, "orderId")}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-2xs transition-all"
                            title="คัดลอก Transaction ID"
                        >
                            {copiedKey === "orderId" ? (
                                <FaCheck className="text-emerald-600 text-[11px]" />
                            ) : (
                                <FaCopy className="text-gray-500 text-[11px]" />
                            )}
                            <span>ID: <span className="font-mono text-gray-600">{order.id}</span></span>
                        </button>
                        {order.rewardId && (
                            <a
                                href={`/coupon/${order.rewardId}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-300 hover:bg-gray-50 rounded-md text-xs font-bold text-rose-600 shadow-2xs transition-colors"
                            >
                                เปิดหน้าระบบคูปอง <TbExternalLink className="text-[11px]" />
                            </a>
                        )}
                    </div>
                </div>

                {/* Top Half: 2-Column Split (Mission Details vs Reward Coupon) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                    {/* Left Column (7 cols): Mission Criteria & Info */}
                    <div className="lg:col-span-7 p-5 space-y-4">
                        <div className="flex items-start gap-3.5">
                            {mission.image ? (
                                <img
                                    src={mission.image}
                                    alt={mission.name}
                                    className="w-14 h-14 object-cover rounded-md border border-gray-200 shrink-0 bg-white"
                                />
                            ) : (
                                <div className="w-14 h-14 rounded-md bg-rose-100 text-rose-600 flex items-center justify-center text-xl shrink-0">
                                    <FaGift />
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                        ภารกิจ
                                    </span>
                                </div>
                                <h3 className="text-base font-bold text-gray-900 leading-snug">
                                    {mission.name || "ภารกิจไม่ระบุชื่อ"}
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                    {mission.info || "ไม่มีคำอธิบายเพิ่มเติม"}
                                </p>
                            </div>
                        </div>

                        {/* Condition & Meta Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                            <div className="p-3 bg-gray-50/70 rounded-md border border-gray-200/80">
                                <span className="text-xs font-semibold text-gray-500 block mb-1">
                                    เงื่อนไขภารกิจ
                                </span>
                                <div className="text-xs font-bold text-gray-800">
                                    {mission.type === "amountInOneOrder" ? (
                                        <div className="flex items-center gap-1.5 text-gray-700">
                                            <FaTicket className="text-rose-500 text-xs" />
                                            <span>ซื้อสลากจำนวน <b>{mission.requiredLotto || 1}</b> ใบ ใน 1 คำสั่งซื้อ</span>
                                        </div>
                                    ) : mission.type === "lastTwoInOneOrder" ? (
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5 text-gray-700">
                                                <FaTicket className="text-rose-500 text-xs" />
                                                <span>ซื้อสลากจำนวน <b>{mission.requiredLotto || 1}</b> ใบ (1 ออเดอร์)</span>
                                            </div>
                                            {mission.lastTwo && (
                                                <div className="flex items-center gap-1.5 text-blue-700">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                    <span>เลขท้าย 2 ตัว: <span className="font-mono bg-blue-100 px-1.5 py-0.2 rounded text-blue-900 font-bold">{mission.lastTwo}</span></span>
                                                </div>
                                            )}
                                        </div>
                                    ) : mission.type === "lastTwoInMultipleOrders" ? (
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5 text-gray-700">
                                                <FaTicket className="text-rose-500 text-xs" />
                                                <span>ซื้อสลากสะสมครบ <b>{mission.requiredLotto || 1}</b> ใบ (สะสมได้)</span>
                                            </div>
                                            {mission.lastTwo && (
                                                <div className="flex items-center gap-1.5 text-indigo-700">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                                    <span>เลขท้าย 2 ตัว: <span className="font-mono bg-indigo-100 px-1.5 py-0.2 rounded text-indigo-900 font-bold">{mission.lastTwo}</span></span>
                                                </div>
                                            )}
                                        </div>
                                    ) : mission.type === "setInMultipleOrder" ? (
                                        <div className="flex items-center gap-1.5 text-emerald-700">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                            <span>ซื้อหวยชุด <b>{mission.set || 2}</b> ใบ สะสมครบ <b>{mission.requiredSet || 1}</b> ชุด</span>
                                        </div>
                                    ) : mission.type === "custom" ? (
                                        <span className="text-purple-700">ภารกิจกำหนดเอง / อัปโหลดหลักฐานรูปภาพ</span>
                                    ) : mission.type === "buy" ? (
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5 text-gray-700">
                                                <FaTicket className="text-rose-500 text-xs" />
                                                <span>ซื้อสลากจำนวน <b>{mission.requiredLotto || 1}</b> ใบ</span>
                                            </div>
                                            {mission.lastTwo && (
                                                <div className="flex items-center gap-1.5 text-amber-700">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                                    <span>เลขท้าย 2 ตัว: <span className="font-mono bg-amber-100 px-1.5 py-0.2 rounded text-amber-900 font-bold">{mission.lastTwo}</span></span>
                                                </div>
                                            )}
                                            {mission.set && (
                                                <div className="flex items-center gap-1.5 text-blue-700">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                    <span>ซื้อแบบชุด: <b>{mission.requiredSet || 1}</b> ชุด ({mission.set} ใบ/ชุด)</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <span>ตามที่กำหนดในระบบ</span>
                                    )}
                                </div>
                            </div>

                            <div className="p-3 bg-gray-50/70 rounded-md border border-gray-200/80">
                                <span className="text-xs font-semibold text-gray-500 block mb-1">
                                    วัน-เวลาทำสำเร็จ
                                </span>
                                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800">
                                    <FaClock className="text-gray-400 text-xs" />
                                    <span>{formatDateTime(order.createdAt)}</span>
                                </div>
                                {order.roundDate && (
                                    <div className="text-[11px] font-semibold text-gray-500 mt-1">
                                        ประจำงวด: <span className="text-gray-800 font-bold">{order.roundDate}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Evidence Thumbnail (if present) */}
                        {order.evidenceImage && (
                            <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <img
                                        src={order.evidenceImage}
                                        alt="หลักฐานภารกิจ"
                                        onClick={() => setSelectedImage(order.evidenceImage)}
                                        className="w-12 h-12 object-cover rounded-md border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                    />
                                    <div>
                                        <span className="text-xs font-bold text-gray-800 flex items-center gap-1">
                                            <FaImages className="text-rose-500" /> มีรูปภาพหลักฐาน
                                        </span>
                                        <button
                                            onClick={() => setSelectedImage(order.evidenceImage)}
                                            className="text-[11px] font-bold text-rose-600 hover:underline inline-flex items-center gap-1 mt-0.5"
                                        >
                                            คลิกเพื่อดูภาพขยาย <TbExternalLink />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column (5 cols): Reward / Coupon Details */}
                    <div className="lg:col-span-5 p-5 space-y-3 bg-gray-50/30">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                                <FaGift className="text-rose-500" /> ของรางวัลคูปองที่ได้รับ
                            </span>
                            <span className="font-bold px-2 py-0.5 rounded text-[11px] bg-emerald-100 text-emerald-800">
                                {couponData?.status === "used"
                                    ? "ใช้งานแล้ว"
                                    : couponData?.status === "expired"
                                        ? "หมดอายุ"
                                        : "พร้อมใช้งาน (Active)"}
                            </span>
                        </div>

                        {loadingCoupon ? (
                            <div className="py-6 flex flex-col items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-rose-500 border-t-transparent"></div>
                                <span className="text-xs font-semibold text-gray-400">กำลังดึงข้อมูลคูปอง...</span>
                            </div>
                        ) : (
                            <>
                                {/* Compact Coupon Card Banner */}
                                <div className="relative p-3.5 rounded-md bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-xs overflow-hidden flex items-center justify-between gap-3">
                                    <div>
                                        <span className="inline-block px-1.5 py-0.2 rounded text-[9px] font-extrabold bg-white/20 uppercase tracking-wider mb-0.5">
                                            {couponData?.coupon?.type || missionReward.type || "COUPON"}
                                        </span>
                                        <h4 className="text-sm font-bold text-white leading-tight">
                                            {couponData?.coupon?.name || missionReward.name || "คูปองส่วนลด"}
                                        </h4>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-xl font-black text-white">
                                            {couponData?.coupon?.type === "PERCENT" || missionReward.type === "PERCENT" ? (
                                                <span>{couponData?.coupon?.discount || missionReward.discount || 0}%</span>
                                            ) : (
                                                <span>฿{couponData?.coupon?.amount || missionReward.amount || missionReward.discount || 0}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Compact Coupon Metadata Rows */}
                                <div className="space-y-1.5 pt-1 text-xs">
                                    <div className="flex items-center justify-between p-2 bg-white rounded-md border border-gray-200">
                                        <span className="font-semibold text-gray-500">Coupon Order ID</span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-mono font-bold text-gray-800">{order.rewardId || "-"}</span>
                                            {order.rewardId && (
                                                <button
                                                    onClick={() => handleCopy(order.rewardId, "couponId")}
                                                    className="p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
                                                    title="คัดลอก Coupon ID"
                                                >
                                                    {copiedKey === "couponId" ? (
                                                        <FaCheck className="text-emerald-600 text-[11px]" />
                                                    ) : (
                                                        <FaCopy className="text-[11px]" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {couponData?.coupon?.expireAt && (
                                        <div className="flex items-center justify-between p-2 bg-white rounded-md border border-gray-200">
                                            <span className="font-semibold text-gray-500">วันหมดอายุคูปอง</span>
                                            <span className="font-bold text-gray-800">
                                                {formatDateTime(couponData.coupon.expireAt)}
                                            </span>
                                        </div>
                                    )}

                                    {couponData?.coupon?.minPurchase > 0 && (
                                        <div className="flex items-center justify-between p-2 bg-white rounded-md border border-gray-200">
                                            <span className="font-semibold text-gray-500">ยอดซื้อขั้นต่ำ</span>
                                            <span className="font-bold text-gray-800">
                                                ฿{Number(couponData.coupon.minPurchase).toLocaleString()} บาท
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Bottom Half: Referenced Lottery Orders (Directly inside same panel) */}
                <div className="border-t border-gray-200 bg-gray-50/40 p-5 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <FaReceipt className="text-rose-500 text-sm" />
                            <h3 className="text-sm font-bold text-gray-900">
                                คำสั่งซื้อสลากที่ใช้แลกรางวัลในภารกิจนี้
                            </h3>
                        </div>

                        {hasRefOrders && (
                            <div className="flex items-center gap-2 self-start sm:self-auto text-xs font-semibold">
                                <span className="px-2.5 py-0.5 bg-white border border-gray-200 rounded-md text-gray-700 shadow-2xs">
                                    รวม <b>{lotteryOrders.length}</b> ออเดอร์
                                </span>
                                <span className="px-2.5 py-0.5 bg-rose-50 border border-rose-200 rounded-md text-rose-700 shadow-2xs">
                                    สลาก <b>{currentTotalLottoTickets}</b> ใบ (฿{currentTotalLottoPrice.toLocaleString()})
                                </span>
                            </div>
                        )}
                    </div>

                    {loadingLotteryOrders ? (
                        <div className="py-6 flex flex-col items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-rose-500 border-t-transparent"></div>
                            <p className="text-xs font-semibold text-gray-400">กำลังดึงข้อมูลคำสั่งซื้อสลาก...</p>
                        </div>
                    ) : !hasRefOrders ? (
                        <div className="p-4 bg-white rounded-md border border-gray-200 text-center">
                            <p className="text-xs font-bold text-gray-500">ไม่มีข้อมูลเลขอ้างอิงคำสั่งซื้อสลาก</p>
                            <p className="text-[11px] font-semibold text-gray-400 mt-0.5">
                                ภารกิจนี้อาจเป็นภารกิจพิเศษ หรือแลกรางวัลผ่านการอัปโหลดหลักฐานรูปภาพ
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {lotteryOrders.map((lotteryOrder, idx) => {
                                const tickets = lotteryOrder.lotteries || [];
                                const orderShortId =
                                    lotteryOrder.shortId || lotteryOrder.id?.slice(-5).toUpperCase();

                                return (
                                    <div
                                        key={lotteryOrder.id || idx}
                                        className="p-3 bg-white rounded-md border border-gray-200 shadow-2xs space-y-2.5 transition-all hover:border-gray-300"
                                    >
                                        {/* Order Meta Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-2">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-mono text-xs font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                                                    #{orderShortId}
                                                </span>
                                                <span className="text-[11px] font-semibold text-gray-500">
                                                    {formatDateTime(lotteryOrder.createdAt)}
                                                </span>
                                                {lotteryOrder.status && (
                                                    <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                                        {lotteryOrder.status}
                                                    </span>
                                                )}
                                                {lotteryOrder.paymentMethod && (
                                                    <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-gray-100 text-gray-700">
                                                        {lotteryOrder.paymentMethod}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <span className="text-xs font-semibold text-gray-600">
                                                    จำนวน: <b className="text-gray-900">{tickets.length || lotteryOrder.size || 0}</b> ใบ | ยอด:{" "}
                                                    <b className="text-rose-600">฿{Number(lotteryOrder.totalPrice || 0).toLocaleString()}</b> บาท
                                                </span>
                                                <a
                                                    href={`/orders/${lotteryOrder.id}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-md bg-gray-50 border border-gray-300 text-gray-700 hover:bg-gray-100 transition-all"
                                                >
                                                    ดูออเดอร์สลาก <TbExternalLink className="text-[10px]" />
                                                </a>
                                            </div>
                                        </div>

                                        {/* Lottery Tickets Grid */}
                                        {tickets.length > 0 ? (
                                            <div className="flex flex-wrap gap-1.5 pt-0.5">
                                                {tickets.map((t, tIdx) => {
                                                    const numStr = String(t.number || t.id || t || "");
                                                    const isMatchLastTwo =
                                                        mission.lastTwo &&
                                                        numStr.endsWith(String(mission.lastTwo));

                                                    return (
                                                        <div
                                                            key={t.id || tIdx}
                                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-bold border transition-all ${isMatchLastTwo
                                                                ? "bg-amber-100 text-amber-900 border-amber-300 ring-1 ring-amber-400/40"
                                                                : "bg-gray-50 text-gray-800 border-gray-200"
                                                                }`}
                                                            title={isMatchLastTwo ? "ตรงกับเงื่อนไขเลขท้ายภารกิจ" : ""}
                                                        >
                                                            <FaTicket
                                                                className={`text-[9px] ${isMatchLastTwo ? "text-amber-600" : "text-gray-400"
                                                                    }`}
                                                            />
                                                            <span>{numStr}</span>
                                                            {isMatchLastTwo && (
                                                                <span className="px-1 py-0.2 rounded bg-amber-500 text-white text-[8px] font-sans font-bold">
                                                                    ตรงเงื่อนไข
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-xs font-semibold text-gray-400">
                                                {lotteryOrder.isFallback
                                                    ? `Order ID: ${lotteryOrder.id} (ไม่พบรายละเอียด)`
                                                    : "ไม่มีรายการเลขสลากในคำสั่งซื้อ"}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ========================================================================= */}
            {/* Comprehensive Customer Lifetime History Section (Missions, Coupons, Lotto) */}
            {/* ========================================================================= */}
            <div className="bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
                {/* Section Header */}
                <div className="p-5 border-b border-gray-200 bg-gray-50/70 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-md bg-navy text-white flex items-center justify-center font-bold text-base shadow-sm">
                            <FaClockRotateLeft />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                ประวัติกิจกรรมทั้งหมดของลูกค้ารายนี้{" "}
                                <span className="text-rose-600 font-mono text-lg">
                                    {user?.userId ? `(#${user.userId})` : ""}
                                </span>
                            </h2>
                            <p className="text-xs text-gray-500 font-semibold">
                                รวมประวัติภารกิจ คูปอง และคำสั่งซื้อสลากทั้งหมดที่เคยทำรายการไว้ในระบบ
                            </p>
                        </div>
                    </div>

                    {/* Quick Stats Summary */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="px-3 py-1.5 bg-white border border-gray-200 rounded-md shadow-2xs text-xs font-semibold text-gray-700">
                            🎯 ภารกิจ: <b className="text-rose-600">{userMissions.length}</b>
                        </div>
                        <div className="px-3 py-1.5 bg-white border border-gray-200 rounded-md shadow-2xs text-xs font-semibold text-gray-700">
                            🎟️ คูปอง: <b className="text-rose-600">{userCoupons.length}</b>
                        </div>
                        <div className="px-3 py-1.5 bg-white border border-gray-200 rounded-md shadow-2xs text-xs font-semibold text-gray-700">
                            🧾 สลาก: <b className="text-rose-600">{totalUserLottoTickets}</b> ใบ (฿{totalUserLottoSpent.toLocaleString()})
                        </div>
                    </div>
                </div>

                {/* Tab Controls & Search Bar */}
                <div className="p-4 border-b border-gray-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    {/* Tabs */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                        <button
                            onClick={() => setActiveTab("missions")}
                            className={`flex items-center gap-2 py-2 px-4 rounded-md text-sm font-bold transition-all ${activeTab === "missions"
                                ? "bg-rose-500 text-white shadow-sm"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                        >
                            <FaLayerGroup className="text-xs" />
                            <span>ประวัติภารกิจ</span>
                            <span
                                className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === "missions"
                                    ? "bg-white/30 text-white"
                                    : "bg-gray-300 text-gray-700"
                                    }`}
                            >
                                {userMissions.length}
                            </span>
                        </button>

                        <button
                            onClick={() => setActiveTab("coupons")}
                            className={`flex items-center gap-2 py-2 px-4 rounded-md text-sm font-bold transition-all ${activeTab === "coupons"
                                ? "bg-rose-500 text-white shadow-sm"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                        >
                            <FaGift className="text-xs" />
                            <span>ประวัติคูปอง</span>
                            <span
                                className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === "coupons"
                                    ? "bg-white/30 text-white"
                                    : "bg-gray-300 text-gray-700"
                                    }`}
                            >
                                {userCoupons.length}
                            </span>
                        </button>

                        <button
                            onClick={() => setActiveTab("lotteryOrders")}
                            className={`flex items-center gap-2 py-2 px-4 rounded-md text-sm font-bold transition-all ${activeTab === "lotteryOrders"
                                ? "bg-rose-500 text-white shadow-sm"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                        >
                            <FaReceipt className="text-xs" />
                            <span>ประวัติคำสั่งซื้อสลาก</span>
                            <span
                                className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === "lotteryOrders"
                                    ? "bg-white/30 text-white"
                                    : "bg-gray-300 text-gray-700"
                                    }`}
                            >
                                {userLotteryOrders.length}
                            </span>
                        </button>
                    </div>

                    {/* Search inside History */}
                    <div className="relative w-full sm:w-64">
                        <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                            type="text"
                            placeholder="ค้นหาในประวัตินี้..."
                            value={historySearch}
                            onChange={(e) => setHistorySearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-300 rounded-md text-xs font-semibold focus:bg-white focus:outline-none focus:border-rose-500"
                        />
                    </div>
                </div>

                {/* Tab Content Container */}
                <div className="p-5">
                    {/* 1. Tab Missions */}
                    {activeTab === "missions" && (
                        <div>
                            {loadingUserMissions ? (
                                <div className="py-12 flex flex-col items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-8 w-8 border-3 border-rose-500 border-t-transparent"></div>
                                    <span className="text-xs font-semibold text-gray-400">กำลังโหลดประวัติภารกิจ...</span>
                                </div>
                            ) : filteredUserMissions.length === 0 ? (
                                <div className="py-12 text-center text-gray-400 text-sm font-semibold">
                                    ไม่พบรายการประวัติภารกิจ
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-gray-100 text-gray-600 font-bold border-b border-gray-200">
                                            <tr>
                                                <th className="p-3">รหัสคำขอ</th>
                                                <th className="p-3">ชื่อภารกิจ</th>
                                                <th className="p-3">ประเภท</th>
                                                <th className="p-3">งวดสลาก</th>
                                                <th className="p-3">ของรางวัล</th>
                                                <th className="p-3">วัน-เวลาสำเร็จ</th>
                                                <th className="p-3 text-right">ดำเนินการ</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 font-semibold">
                                            {filteredUserMissions.map((m) => {
                                                const isCurrentOrder = m.id === order.id;
                                                return (
                                                    <tr
                                                        key={m.id}
                                                        className={`hover:bg-gray-50/80 transition-colors ${isCurrentOrder ? "bg-rose-50/50 font-bold" : ""
                                                            }`}
                                                    >
                                                        <td className="p-3">
                                                            <span className="font-mono text-rose-600 font-bold">
                                                                #{m.shortId || m.id?.slice(-5).toUpperCase()}
                                                            </span>
                                                            {isCurrentOrder && (
                                                                <span className="ml-2 px-1.5 py-0.2 rounded bg-rose-500 text-white text-[10px]">
                                                                    ปัจจุบัน
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="p-3 text-gray-900 font-bold">
                                                            {m.mission?.name || "-"}
                                                        </td>
                                                        <td className="p-3">{getMissionTypeBadge(m.mission?.type)}</td>
                                                        <td className="p-3 text-gray-600">{m.roundDate || "-"}</td>
                                                        <td className="p-3 text-emerald-700 font-bold">
                                                            {m.mission?.reward?.name || "-"}
                                                        </td>
                                                        <td className="p-3 text-gray-500">{formatDateTime(m.createdAt)}</td>
                                                        <td className="p-3 text-right">
                                                            {!isCurrentOrder ? (
                                                                <Link
                                                                    to={`/mission/${m.id}`}
                                                                    className="inline-flex items-center gap-1 text-xs font-bold text-rose-600 hover:underline"
                                                                >
                                                                    ดูรายละเอียด <TbExternalLink />
                                                                </Link>
                                                            ) : (
                                                                <span className="text-gray-400 text-xs">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 2. Tab Coupons */}
                    {activeTab === "coupons" && (
                        <div>
                            {loadingUserCoupons ? (
                                <div className="py-12 flex flex-col items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-8 w-8 border-3 border-rose-500 border-t-transparent"></div>
                                    <span className="text-xs font-semibold text-gray-400">กำลังโหลดประวัติคูปอง...</span>
                                </div>
                            ) : filteredUserCoupons.length === 0 ? (
                                <div className="py-12 text-center text-gray-400 text-sm font-semibold">
                                    ไม่พบรายการคูปองของลูกค้ารายนี้
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-gray-100 text-gray-600 font-bold border-b border-gray-200">
                                            <tr>
                                                <th className="p-3">รหัสคูปอง</th>
                                                <th className="p-3">ชื่อคูปอง / โปรโมชั่น</th>
                                                <th className="p-3">ประเภท / มูลค่า</th>
                                                <th className="p-3">สถานะ</th>
                                                <th className="p-3">วันหมดอายุ</th>
                                                <th className="p-3">วันที่ได้รับ</th>
                                                <th className="p-3 text-right">ลิงก์คูปอง</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 font-semibold">
                                            {filteredUserCoupons.map((c) => {
                                                const coupon = c.coupon || {};
                                                const isCurrentReward = c.id === order.rewardId;

                                                return (
                                                    <tr
                                                        key={c.id}
                                                        className={`hover:bg-gray-50/80 transition-colors ${isCurrentReward ? "bg-rose-50/50 font-bold" : ""
                                                            }`}
                                                    >
                                                        <td className="p-3">
                                                            <span className="font-mono text-gray-800 font-bold">
                                                                #{c.shortId || c.id?.slice(-5).toUpperCase()}
                                                            </span>
                                                            {isCurrentReward && (
                                                                <span className="ml-2 px-1.5 py-0.2 rounded bg-rose-500 text-white text-[10px]">
                                                                    จากภารกิจนี้
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="p-3 text-gray-900 font-bold">{coupon.name || "-"}</td>
                                                        <td className="p-3">
                                                            <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 font-bold border border-rose-200">
                                                                {coupon.type === "PERCENT"
                                                                    ? `ลด ${coupon.discount || 0}%`
                                                                    : `฿${Number(coupon.amount || coupon.discount || 0).toLocaleString()} บาท`}
                                                            </span>
                                                        </td>
                                                        <td className="p-3">
                                                            <span
                                                                className={`px-2 py-0.5 rounded text-[11px] font-bold ${c.status === "used"
                                                                    ? "bg-gray-100 text-gray-600"
                                                                    : c.status === "expired"
                                                                        ? "bg-red-100 text-red-700"
                                                                        : "bg-emerald-100 text-emerald-800"
                                                                    }`}
                                                            >
                                                                {c.status === "used"
                                                                    ? "ใช้งานแล้ว"
                                                                    : c.status === "expired"
                                                                        ? "หมดอายุ"
                                                                        : "พร้อมใช้ (Active)"}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-gray-600">{formatDateTime(coupon.expireAt)}</td>
                                                        <td className="p-3 text-gray-500">{formatDateTime(c.createdAt)}</td>
                                                        <td className="p-3 text-right">
                                                            <a
                                                                href={`/coupon/${c.id}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-1 text-xs font-bold text-rose-600 hover:underline"
                                                            >
                                                                เปิดดูคูปอง <TbExternalLink />
                                                            </a>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 3. Tab Lottery Orders */}
                    {activeTab === "lotteryOrders" && (
                        <div>
                            {loadingUserLotteryOrders ? (
                                <div className="py-12 flex flex-col items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-8 w-8 border-3 border-rose-500 border-t-transparent"></div>
                                    <span className="text-xs font-semibold text-gray-400">กำลังโหลดคำสั่งซื้อสลาก...</span>
                                </div>
                            ) : filteredUserLotteryOrders.length === 0 ? (
                                <div className="py-12 text-center text-gray-400 text-sm font-semibold">
                                    ไม่พบรายการคำสั่งซื้อสลากของลูกค้ารายนี้
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-gray-100 text-gray-600 font-bold border-b border-gray-200">
                                            <tr>
                                                <th className="p-3">รหัสคำสั่งซื้อ</th>
                                                <th className="p-3">งวดสลาก</th>
                                                <th className="p-3">จำนวนสลาก</th>
                                                <th className="p-3">ยอดชำระ (บาท)</th>
                                                <th className="p-3">สถานะ</th>
                                                <th className="p-3">การชำระ</th>
                                                <th className="p-3">วัน-เวลาสั่งซื้อ</th>
                                                <th className="p-3 text-right">ดำเนินการ</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 font-semibold">
                                            {filteredUserLotteryOrders.map((o) => {
                                                const isReferencedInThisMission =
                                                    Array.isArray(order.orderReference) &&
                                                    order.orderReference.includes(o.id);
                                                const lottoTickets = o.lotteries || [];

                                                return (
                                                    <tr
                                                        key={o.id}
                                                        className={`hover:bg-gray-50/80 transition-colors ${isReferencedInThisMission ? "bg-amber-50/60 font-bold" : ""
                                                            }`}
                                                    >
                                                        <td className="p-3">
                                                            <span className="font-mono text-gray-900 font-bold">
                                                                #{o.shortId || o.id?.slice(-5).toUpperCase()}
                                                            </span>
                                                            {isReferencedInThisMission && (
                                                                <span className="ml-2 px-1.5 py-0.2 rounded bg-amber-500 text-white text-[10px]">
                                                                    ใช้ในภารกิจนี้
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="p-3 text-gray-700">{o.roundDate || "-"}</td>
                                                        <td className="p-3">
                                                            <span className="font-bold text-gray-900">
                                                                {lottoTickets.length || o.size || 0} ใบ
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-rose-600 font-bold">
                                                            ฿{Number(o.totalPrice || 0).toLocaleString()}
                                                        </td>
                                                        <td className="p-3">
                                                            <span
                                                                className={`px-2 py-0.5 rounded text-[11px] font-bold ${o.status === "APPROVE" || o.status === "PAID"
                                                                    ? "bg-emerald-100 text-emerald-800"
                                                                    : o.status === "CANCEL"
                                                                        ? "bg-rose-100 text-rose-800"
                                                                        : "bg-blue-100 text-blue-800"
                                                                    }`}
                                                            >
                                                                {o.status || "PENDING"}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-gray-600">{o.paymentMethod || "-"}</td>
                                                        <td className="p-3 text-gray-500">{formatDateTime(o.createdAt)}</td>
                                                        <td className="p-3 text-right">
                                                            <a
                                                                href={`/orders/${o.id}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-1 text-xs font-bold text-rose-600 hover:underline"
                                                            >
                                                                ดูออเดอร์สลาก <TbExternalLink />
                                                            </a>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Lightbox Image Preview Modal */}
            {selectedImage && (
                <div
                    onClick={() => setSelectedImage(null)}
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="relative bg-white rounded-md max-w-4xl max-h-[90vh] overflow-hidden p-2 shadow-2xl flex flex-col items-center"
                    >
                        <div className="w-full flex justify-between items-center p-2 border-b border-gray-100">
                            <span className="text-sm font-bold text-gray-800">ภาพหลักฐานภารกิจ</span>
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="px-2.5 py-1 rounded-md text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-700"
                            >
                                ปิด (Close)
                            </button>
                        </div>
                        <img
                            src={selectedImage}
                            alt="ภาพหลักฐานขยาย"
                            className="max-w-full max-h-[80vh] object-contain rounded-md mt-2"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MissionDetail;
